"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
require("mapbox-gl/dist/mapbox-gl.css");
var _react = _interopRequireWildcard(require("react"));
var _reactMapGl = require("react-map-gl");
var _geojsonExtent = _interopRequireDefault(require("@mapbox/geojson-extent"));
var _constants = require("../constants");
var _useChartData = require("../hooks/useChartData");
var _useColor = require("../hooks/useColor");
var _BaseMap = _interopRequireDefault(require("./BaseMap"));
var _turf = require("@turf/turf");
var _useFilter = require("../hooks/useFilter");
var _lodash = _interopRequireDefault(require("lodash.clonedeep"));
function _getRequireWildcardCache(nodeInterop) { if (typeof WeakMap !== "function") return null; var cacheBabelInterop = new WeakMap(); var cacheNodeInterop = new WeakMap(); return (_getRequireWildcardCache = function (nodeInterop) { return nodeInterop ? cacheNodeInterop : cacheBabelInterop; })(nodeInterop); }
function _interopRequireWildcard(obj, nodeInterop) { if (!nodeInterop && obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(nodeInterop); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }
const ConnectionMap = /*#__PURE__*/(0, _react.memo)(chartProps => {
  const {
    chart,
    boundaries,
    chartColors
  } = chartProps;
  const colorConfig = chart.colors || chartColors;
  if (colorConfig && !colorConfig.settings) {
    colorConfig.settings = {
      direction: "ascending",
      divergingMidPoint: 0,
      steps: 6
    };
  }
  const {
    yCol,
    tooltipCol,
    seriesCol,
    componentProps,
    regions
  } = chart;
  const {
    colorScheme = "sequential"
  } = componentProps;
  const {
    filter,
    filterComponent
  } = (0, _useFilter.useFilter)(chartProps);
  const {
    data
  } = (0, _useChartData.useChartData)(chart, filter);
  const {
    calculateColorStops
  } = (0, _useColor.useColor)(colorConfig, colorScheme);
  const vBoundaries = boundaries[regions.setup.version];
  const lines = (0, _react.useMemo)(() => {
    if (!vBoundaries) {
      return null;
    }
    if (regions !== null && regions !== void 0 && regions.main && regions !== null && regions !== void 0 && regions.related && vBoundaries[regions.main]) {
      const mainCoords = (0, _turf.center)(vBoundaries[regions.main].feature).geometry.coordinates;
      const lines = data.map(d => {
        return d.points.filter(p => p[_constants.COL_RELATED_GEO_ID] && vBoundaries[p[_constants.COL_RELATED_GEO_ID]]).map(p => {
          const region = vBoundaries[p[_constants.COL_RELATED_GEO_ID]];
          const properties = {
            ...p,
            [yCol]: colorScheme !== "categorical" ? Number(p[yCol]) : p[yCol]
          };
          const coords = (0, _turf.centerOfMass)(region.feature).geometry.coordinates;
          const line = (0, _turf.lineString)([mainCoords, coords], properties);
          const lineDistance = (0, _turf.length)(line);
          const arc = [];
          const steps = 20;
          for (let i = 0; i < lineDistance; i += lineDistance / steps) {
            const segment = (0, _turf.along)(line, i);
            arc.push(segment.geometry.coordinates);
          }
          arc.push(coords);
          line.geometry.coordinates = arc;
          return line;
        });
      }).flat();
      return (0, _turf.featureCollection)(lines);
    }
  }, [data, regions, vBoundaries, colorScheme]);
  const areas = (0, _react.useMemo)(() => {
    const features = [];
    if (regions !== null && regions !== void 0 && regions.main && vBoundaries[regions.main]) {
      features.push(vBoundaries[regions.main].feature);
    } else if (regions !== null && regions !== void 0 && regions.segments) {
      const segmentRegions = regions.segments.map(geoId => vBoundaries[geoId]).filter(Boolean);
      features.push(...segmentRegions.map(region => region.feature));
    }
    if (regions !== null && regions !== void 0 && regions.related && lines) {
      for (const geoId of regions.related) {
        const region = vBoundaries[geoId];
        if (!region) {
          continue;
        }
        const line = lines.features.find(feature => feature.properties[_constants.COL_RELATED_GEO_ID] === geoId);
        if (line) {
          const feature = (0, _lodash.default)(region.feature);
          feature.properties = {
            ...region.feature.properties,
            ...line.properties
          };
          features.push(feature);
        }
      }
    }
    if (regions !== null && regions !== void 0 && regions.peers) {
      const peerRegions = regions.peers.map(geoId => vBoundaries[geoId]).filter(Boolean);
      features.push(...peerRegions.map(region => region.feature));
    }
    return (0, _turf.featureCollection)(features);
  }, [regions, vBoundaries, lines]);
  const sortedValues = (0, _react.useMemo)(() => {
    if (lines) {
      if (colorScheme !== "categorical") {
        return lines.features.map(f => Number(f.properties[yCol])).sort((a, b) => a - b);
      } else {
        return Array.from(new Set(lines.features.map(f => Number(f.properties[yCol])))).sort();
      }
    }
    return [];
  }, [yCol, colorScheme, lines]);
  const colorStops = (0, _react.useMemo)(() => calculateColorStops(sortedValues), [sortedValues]);
  const flatColorStops = colorStops.flat();
  const createPopup = (props, mapRef) => {
    var _chart$dataView$colum;
    if (areas.features.length === 0) {
      return;
    }
    const featureCollection = {
      type: "FeatureCollection",
      features: areas.features.filter(feature => feature.properties[_constants.COL_RELATED_GEO_ID] === props[_constants.COL_RELATED_GEO_ID])
    };
    const bounds = (0, _geojsonExtent.default)(featureCollection);
    const mapBounds = mapRef.current.getMap().getBounds().toArray();
    const mapHeight = mapBounds[1][1] - mapBounds[0][1];
    const topOffset = (bounds[3] - mapBounds[0][1]) / mapHeight;
    const bottomOffset = (bounds[1] - mapBounds[0][1]) / mapHeight;
    let lng = (bounds[0] + bounds[2]) / 2;
    let lat = bounds[3];
    let anchor = "bottom";
    if (topOffset >= 0.8) {
      lat = bounds[1];
      anchor = "top";
      if (bottomOffset <= 0.15) {
        lat = (bounds[3] + bounds[1]) / 2;
        anchor = "center";
      }
    }
    const label = ((_chart$dataView$colum = chart.dataView.columns[seriesCol].categories[props[_constants.COL_RELATED_GEO_ID]]) === null || _chart$dataView$colum === void 0 ? void 0 : _chart$dataView$colum.label) || "";
    return {
      geoId: props[_constants.COL_RELATED_GEO_ID],
      label,
      tooltipValue: props[tooltipCol],
      lng,
      lat,
      anchor
    };
  };
  if (!vBoundaries || !lines) {
    return null;
  }
  return /*#__PURE__*/_react.default.createElement(_BaseMap.default, (0, _extends2.default)({}, chartProps, {
    createPopup: createPopup,
    sortedValues: sortedValues,
    flatColorStops: flatColorStops,
    areas: areas,
    filterComponent: filterComponent
  }), (areas === null || areas === void 0 ? void 0 : areas.features.length) > 0 && /*#__PURE__*/_react.default.createElement(_reactMapGl.Source, {
    id: "".concat(chart.id, "|related"),
    type: "geojson",
    data: areas
  }, /*#__PURE__*/_react.default.createElement(_reactMapGl.Layer, {
    id: "".concat(chart.id, "|boundary-layer"),
    type: "line",
    paint: {
      "line-width": 1,
      "line-color": "#333",
      "line-opacity": 0.5
    }
  }), /*#__PURE__*/_react.default.createElement(_reactMapGl.Layer, {
    id: "".concat(chart.id, "|interactive-layer"),
    type: "fill",
    paint: {
      "fill-color": "#999",
      "fill-opacity": 0.1
    }
  })), lines && colorStops.length > 0 && /*#__PURE__*/_react.default.createElement(_reactMapGl.Source, {
    id: "".concat(chart.id, "|connections"),
    type: "geojson",
    data: lines
  }, /*#__PURE__*/_react.default.createElement(_reactMapGl.Layer, {
    id: "".concat(chart.id, "|connections"),
    type: "line",
    layout: {
      "line-cap": "round"
    },
    paint: {
      "line-color": colorScheme === "categorical" ? ["match", ["get", yCol], ...flatColorStops, "#666666"] : {
        property: yCol,
        stops: colorStops
      },
      "line-width": ['interpolate', ['linear'], ['get', yCol], sortedValues.at(0), 2, sortedValues.at(-1), 5]
    }
  })));
});
var _default = ConnectionMap;
exports.default = _default;